import React from 'react';

import styled from 'styled-components';

import { SALES_TALK_TRACKS_LABEL } from 'components/AiCompetitiveIntelligence/constants';
import { VIEW_CATEGORY } from 'components/View/constants';
import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { COMMUNITY_RENTAL_TYPE } from 'constants/rentalTypes';
import { AI_FEATURE_GRADIENT } from 'constants/theme';
import { canAccessCompanyAIOverview, canAccessSalesBattlecardContent, hasPermissionOrAdmin } from 'helpers/permissions';
import { VIEW_PERMISSIONS } from 'permissions/views';
import { useAppSelector } from 'store/hook';

import SidebarMenuItem from './SidebarMenuItem';
import { ACTOR_TYPE } from '../../../constants';
import { useSidebarMenuLogic } from '../hooks';

const AI_BORDER_GRADIENT = 'linear-gradient(to left, #605ffe, rgba(255, 92, 150, 0.69))';

const Container = styled.div`
  position: relative;
  margin: 10px 14px;
  border-radius: 10px;
  overflow: hidden;

  a > div {
    padding-left: 20px;
    padding-right: 20px;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 10px;
    padding: 1px;
    background: ${AI_BORDER_GRADIENT};
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    mask-composite: exclude;
    pointer-events: none;
    z-index: 1;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 5.5px 20px;
  background: ${AI_FEATURE_GRADIENT};
  color: white;
  font-size: 10px;
  letter-spacing: 2px;
`;

const HeaderIcon = styled.img`
  width: 14px;
  height: 14px;
  margin-right: 5px;
`;

const HeaderTitle = styled.span`
  flex: 1;
`;

const HeaderBadge = styled.span`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2px;
  border-radius: 2px;
  background-image: linear-gradient(120deg, #88f, #55f 100%);
  font-size: 8px;
  line-height: 1;
  font-family: var(--font-bold);
  color: white;
  text-transform: uppercase;
`;

const SidebarMenuAIFeatures = () => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const { views, selectedCommunity, communities } = useAppSelector((state) => state.global);
  const {
    showAICompetitiveIntelligence,
    showAISpeedToLead,
    showComingSoonFeatures,
    showAIRecommendations,
    showCommunitiesToWatch,
    showSalesTalkTracks,
    showActionPlans,
    showActionPlansCompanyView,
    showAlerts,
    showPricingData,
  } = useSidebarMenuLogic();

  const communityType =
    selectedCommunity?.type === COMPANY_COMMUNITY_RELATION_TYPE.COMPETITOR
      ? ACTOR_TYPE.COMPETITOR
      : ACTOR_TYPE.COMMUNITY;

  const salesBattlecardView = views?.find((view) => view.category === VIEW_CATEGORY.SALES_BATTLECARD);
  // Temporarily we will prevent showing SalesBattlecard sidebar item completely in case of CCRC. We achieve that
  // by checking the SB view associated rental types - if it contains CCRC -> we hide it.
  const isSalesBattlecardCCRC = salesBattlecardView?.associated_rental_types?.some(
    (rentalType: any) => rentalType.type === COMMUNITY_RENTAL_TYPE.CCRC
  );
  const canViewSalesBattlecard =
    !isSalesBattlecardCCRC &&
    canAccessSalesBattlecardContent(currentUser) &&
    hasPermissionOrAdmin(currentUser, [VIEW_PERMISSIONS.SALES_BATTLECARD.VIEW]);

  const showCompanyAIOverview = !selectedCommunity && canAccessCompanyAIOverview(currentUser, communities.length);

  const showSalesBattlecard =
    showPricingData && Boolean(selectedCommunity) && canViewSalesBattlecard && Boolean(salesBattlecardView);

  if (
    !showAICompetitiveIntelligence &&
    !showAISpeedToLead &&
    !showComingSoonFeatures &&
    !showCommunitiesToWatch &&
    !showSalesTalkTracks &&
    !showActionPlans &&
    !showActionPlansCompanyView &&
    !showAlerts &&
    !showSalesBattlecard &&
    !showAIRecommendations
  ) {
    return null;
  }

  return (
    <Container>
      <Header>
        <HeaderIcon src="/icons/ai-logo-light.svg" alt="" />
        <HeaderTitle>AI FEATURES</HeaderTitle>
        <HeaderBadge>NEW</HeaderBadge>
      </Header>
      {showAICompetitiveIntelligence && selectedCommunity?.type === COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY && (
        <SidebarMenuItem to={`/community/${selectedCommunity.id}/ai-overview`} label="AI Overview" />
      )}
      {showCompanyAIOverview && <SidebarMenuItem to="/ai-overview" label="AI Overview" />}
      {showCommunitiesToWatch && (
        <SidebarMenuItem
          to="communities-to-watch"
          pattern="/communities-to-watch/*"
          label="Communities to Watch"
          badge="NEW"
        />
      )}
      {(showAICompetitiveIntelligence || showAISpeedToLead || showComingSoonFeatures) && (
        <SidebarMenuItem
          to={
            selectedCommunity
              ? `ai-competitive-intelligence?communityId=${selectedCommunity.id}`
              : 'ai-competitive-intelligence'
          }
          pattern="/ai-competitive-intelligence/*"
          label="AI Competitive Intelligence"
        />
      )}
      {showSalesTalkTracks && <SidebarMenuItem to="sales-talk-tracks" label={SALES_TALK_TRACKS_LABEL} />}
      {showAlerts && (
        <SidebarMenuItem
          to={`/community/${selectedCommunity?.id}/alerts`}
          pattern={`/community/${selectedCommunity?.id}/alerts/*`}
          label="Alerts"
        />
      )}
      {(showActionPlans || showActionPlansCompanyView) && (
        <SidebarMenuItem
          to={showActionPlans ? `/community/${selectedCommunity?.id}/action-plans` : '/action-plans-company'}
          pattern={showActionPlans ? `/community/${selectedCommunity?.id}/action-plans/*` : '/action-plans-company/*'}
          label="Action Plans"
        />
      )}
      {showSalesBattlecard && salesBattlecardView && (
        <SidebarMenuItem
          to={`/${communityType}/${selectedCommunity?.id}/view/${salesBattlecardView.id}`}
          label={salesBattlecardView.name}
        />
      )}
      {showAIRecommendations && (
        <SidebarMenuItem to="recommendations" pattern="/recommendations/*" label="AI Insights" />
      )}
    </Container>
  );
};

export default SidebarMenuAIFeatures;
